import { useEffect, useRef } from "react";

import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { useTodaysVisits } from "~/providers/TodaysVisitsProvider";
import { visitResourceNeedsAttention } from "~/utils/visitsHelper";

const CHIME_URL = "/sounds/virtual-visit-invite.wav";

const VirtualVisitChime = () => {
  const providerId = useCurrentOrg()?.providerId;
  const { loading, visitResources } = useTodaysVisits();

  const chime = useRef<HTMLAudioElement>(null);
  const heard = useRef<{ providerId: number, ids: Set<number> } | null>(null);
  const pendingChimeIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!providerId) { return; }

    const removeUnlockListeners = () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };

    const unlock = () => {
      removeUnlockListeners();
      const element = chime.current;
      if (!element) { return; }

      // A muted play on the first user gesture primes the element for browser autoplay rules.
      // If a notification was already blocked, retry it audibly instead of priming again.
      const retryPendingChime = pendingChimeIds.current.size > 0;
      pendingChimeIds.current.clear();
      element.muted = !retryPendingChime;
      element.play().then(() => {
        if (!retryPendingChime) {
          element.pause();
          element.currentTime = 0;
        }
      }).catch(() => {}).finally(() => {
        element.muted = false;
      });
    };

    document.addEventListener("pointerdown", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });

    return removeUnlockListeners;
  }, [providerId]);

  const invitedIds = visitResources
    .filter(vr => visitResourceNeedsAttention(vr, providerId))
    .map(vr => vr.id);

  useEffect(() => {
    if (!providerId || loading) {
      heard.current = null;
      pendingChimeIds.current.clear();
      return;
    }

    const known = heard.current?.providerId === providerId ? heard.current.ids : null;
    heard.current = { providerId, ids: new Set([...(known ?? []), ...invitedIds]) };
    const invitedIdSet = new Set(invitedIds);
    pendingChimeIds.current = new Set(
      [...pendingChimeIds.current].filter(id => invitedIdSet.has(id))
    );

    const newInviteIds = known ? invitedIds.filter(id => !known.has(id)) : [];
    if (newInviteIds.length > 0) {
      newInviteIds.forEach(id => pendingChimeIds.current.add(id));
      chime.current?.play().then(() => {
        newInviteIds.forEach(id => pendingChimeIds.current.delete(id));
      }).catch(() => {});
    } else if (!known) {
      pendingChimeIds.current.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, providerId, invitedIds.join(",")]);

  return providerId ? <audio ref={chime} src={CHIME_URL} preload="auto" /> : null;
};

export default VirtualVisitChime;
